import { Star, Instagram, MapPin, ConciergeBell, Receipt, MessageSquareHeart, Megaphone, Phone, Info, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { BottomSheet } from "@/components/BottomSheet";
import { useUI } from "@/context/UIContext";
import { useOrder } from "@/context/OrderContext";
import { useLang } from "@/context/LanguageContext";
import { restaurantConfig } from "@/config/restaurantConfig";
import { localize } from "@/lib/format";
import { isOnline } from "@/lib/network";

const SERVICE_ERROR_KEY = {
  network: "offlineActionBlocked", rate_limited: "orderRateLimited", table_required: "orderTableRequired",
  table_session_required: "tableSessionRequired",
};

export function ServicesDrawer() {
  const { servicesOpen, closeServices, openWaiter, openFeedback } = useUI();
  const { requestService } = useOrder();
  const { lang, t } = useLang();
  const { links } = restaurantConfig;

  const openUrl = (url) => url && window.open(url, "_blank", "noopener");

  const campaignsText = (restaurantConfig.promotions || []).map((p) => localize(p.title, lang)).join(" · ") || t("campaignsNone");

  const requestBill = async () => {
    if (!isOnline()) {
      toast.error(t("offlineActionBlocked"));
      return;
    }
    const result = await requestService("bill");
    if (!result.ok) {
      toast.error(t(SERVICE_ERROR_KEY[result.reason] || "serviceFailed"));
      return;
    }
    closeServices();
    toast.success(t("billRequested"), { description: t("billRequestedDesc"), icon: <Receipt className="h-4 w-4" /> });
  };

  const tiles = [
    { key: "callWaiter", icon: ConciergeBell, accent: true, onClick: () => { closeServices(); openWaiter(); } },
    { key: "requestBill", icon: Receipt, accent: true, onClick: requestBill },
    { key: "svc_feedback", icon: MessageSquareHeart, onClick: openFeedback },
    { key: "svc_google", icon: Star, onClick: () => openUrl(links.googleReviewUrl) },
    { key: "svc_instagram", icon: Instagram, onClick: () => openUrl(links.instagramUrl) },
    { key: "svc_location", icon: MapPin, onClick: () => openUrl(links.locationUrl) },
    { key: "svc_campaigns", icon: Megaphone, onClick: () => { closeServices(); toast(t("svc_campaigns"), { description: campaignsText }); } },
    { key: "svc_contact", icon: Phone, onClick: () => links.phone && openUrl(`tel:${links.phone.replace(/\s/g, "")}`) },
    { key: "svc_info", icon: Info, onClick: () => { closeServices(); toast(restaurantConfig.name, { description: `${restaurantConfig.branch.name} · ${restaurantConfig.hours}` }); } },
  ];

  return (
    <BottomSheet open={servicesOpen} onClose={closeServices} maxHeight="82vh" testId="services-drawer">
      <div className="px-5 pb-6 pt-1">
        <h2 className="mb-1 font-heading text-xl font-extrabold">{t("services")}</h2>
        <p className="mb-5 text-sm text-muted-foreground">{restaurantConfig.name} · {restaurantConfig.branch.name}</p>
        <div className="grid grid-cols-3 gap-3">
          {tiles.map((tile, i) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.key}
                onClick={tile.onClick}
                className={`flex flex-col items-center gap-2 rounded-2xl border p-3 text-center shadow-soft transition-colors active:scale-95 ${
                  tile.accent ? "border-primary/40 bg-primary/5" : "border-border bg-card"
                }`}
                data-testid={`service-${tile.key}`}
              >
                <span className={`grid h-11 w-11 place-items-center rounded-xl ${tile.accent ? "brand-gradient text-white" : "bg-secondary text-primary"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-[11px] font-semibold leading-tight">{t(tile.key)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </BottomSheet>
  );
}
